import React, { useState, useEffect, useRef } from 'react';
import './_ResumeGateModal.scss';

function ResumeGateModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const modalRef = useRef(null);
  const inputRef = useRef(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setStatus('idle');
      setErrorMessage('');
      // Delay focus slightly to ensure DOM is ready
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 50);
    }
  }, [isOpen]);

  // Handle ESC key to dismiss modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Trap focus inside modal
  useEffect(() => {
    if (!isOpen) return;

    const handleFocusTrap = (e) => {
      if (!modalRef.current) return;
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handleFocusTrap);
    return () => document.removeEventListener('keydown', handleFocusTrap);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage('Please enter your email address.');
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch('/api/resume/request-download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        data = { error: response.status === 404 ? 'API route not found (404). Please try again in a few moments.' : `Server responded with status ${response.status}.` };
      }

      if (response.ok && data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(data.error || 'Failed to request download. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Network error. Unable to connect to the server.');
    }
  };

  return (
    <div
      className="resume-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-hidden={!isOpen}
    >
      <div
        className="resume-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resume-modal-title"
        aria-describedby="resume-modal-desc"
        ref={modalRef}
      >
        <button
          type="button"
          className="resume-modal-close"
          onClick={onClose}
          aria-label="Close download modal"
        >
          &times;
        </button>

        {status === 'success' ? (
          <div className="resume-modal-success">
            <div className="success-icon-wrap" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 id="resume-modal-title" className="success-title">
              Check Your Inbox
            </h2>
            <p id="resume-modal-desc" className="success-desc">
              A secure download link has been emailed to{' '}
              <strong className="recipient-email">{email}</strong>.
            </p>
            <p className="expiry-note">
              ⏱️ The link is valid for <strong>15 minutes</strong>.
            </p>
            <button
              type="button"
              className="resume-modal-btn success-done-btn"
              onClick={onClose}
              autoFocus
            >
              Done
            </button>
          </div>
        ) : (
          <div className="resume-modal-form-wrap">
            <div className="modal-header-tag">Resume Access</div>
            <h2 id="resume-modal-title" className="modal-heading">
              Download Dominic's Resume
            </h2>
            <p id="resume-modal-desc" className="modal-subtext">
              Enter your email address below to receive a secure, time-limited link to download my PDF resume.
            </p>

            {errorMessage && (
              <div className="modal-error-alert" role="alert">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="resume-gate-form">
              <label htmlFor="resume-email-input" className="form-label">
                Your Email Address
              </label>
              <div className="input-row">
                <input
                  id="resume-email-input"
                  ref={inputRef}
                  type="email"
                  className="resume-email-input"
                  placeholder="recruiter@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === 'submitting'}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={onClose}
                  disabled={status === 'submitting'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={status === 'submitting'}
                  aria-busy={status === 'submitting'}
                >
                  {status === 'submitting' ? (
                    <>
                      <span className="spinner" aria-hidden="true" />
                      <span>Sending Link...</span>
                    </>
                  ) : (
                    <span>Send Resume Link</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResumeGateModal;
